/** Job templates list. */
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/app/auth";
import {
  type JobTemplate,
  TEMPLATE_CATEGORIES,
  useJobTemplates,
} from "@/features/admin/maintenance/queries";
import { DataTable } from "@/shared/components/data-table";
import { TableToolbar } from "@/shared/components/table-toolbar";
import { ErrorState, LoadingState, PageHeader } from "@/shared/components/page";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/primitives";

/** Templates list page. */
export function JobTemplatesPage(): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const paged = useJobTemplates({
    q: search.q,
    category: search.type,
    include_inactive: search.open_only,
  });
  const update = (patch: Partial<typeof search>): void =>
    void navigate({
      to: "/admin/job-templates",
      search: { ...search, ...patch },
      resetScroll: false,
    });
  const columns: ColumnDef<JobTemplate>[] = [
    {
      id: "code",
      header: t("app.code"),
      cell: (c) => <span className="font-mono text-xs">{c.row.original.code}</span>,
    },
    {
      id: "name",
      header: t("app.name"),
      cell: (c) => (
        <span className={c.row.original.is_active ? "font-medium" : "font-medium line-through"}>
          {c.row.original.name}
        </span>
      ),
    },
    {
      id: "category",
      header: t("assetTypes.category"),
      cell: (c) => (
        <Badge tone="outline">{t(`jobTemplates.categories.${c.row.original.category}`)}</Badge>
      ),
    },
    { id: "tasks", header: t("workOrders.tasks"), cell: (c) => c.row.original.tasks.length },
    { id: "parts", header: t("workOrders.parts"), cell: (c) => c.row.original.parts.length },
    {
      id: "labour",
      header: t("jobTemplates.labour"),
      cell: (c) => `${c.row.original.est_labour_minutes} min`,
    },
    { id: "plans", header: t("plans.title"), cell: (c) => c.row.original.plan_count ?? 0 },
  ];
  return (
    <div>
      <PageHeader
        title={t("jobTemplates.title")}
        subtitle={t("jobTemplates.subtitle")}
        actions={
          can("maintenance.jobtemplate.manage") ? (
            <Button asChild>
              <Link to="/admin/job-templates/new">{t("jobTemplates.new")}</Link>
            </Button>
          ) : null
        }
      />
      <TableToolbar
        filters={[
          {
            id: "type",
            label: t("assetTypes.category"),
            kind: "select",
            prominent: true,
            value: search.type,
            options: TEMPLATE_CATEGORIES.map((value) => ({
              value,
              label: t(`jobTemplates.categories.${value}`),
            })),
            onChange: (value) => update({ type: typeof value === "string" ? value : undefined }),
            onRemove: () => update({ type: undefined }),
          },
          {
            id: "open_only",
            label: t("jobTemplates.includeInactive"),
            kind: "boolean",
            active: !!search.open_only,
            value: search.open_only,
            onChange: (value) => update({ open_only: value === true || undefined }),
            onRemove: () => update({ open_only: undefined }),
          },
        ]}
        search={search.q}
        searchPlaceholder={t("app.search")}
        searchDescription={t("tablePilot.templateSearchScope")}
        onSearchChange={(q) => update({ q })}
        onReset={() => update({ q: undefined, type: undefined, open_only: undefined })}
      />
      {paged.query.isPending ? (
        <LoadingState />
      ) : paged.query.isError ? (
        <ErrorState error={paged.query.error} onRetry={() => void paged.query.refetch()} />
      ) : (
        <DataTable
          columns={columns}
          data={paged.items}
          minWidth={900}
          getRowId={(x) => x.id}
          rowHref={(x) => `/admin/job-templates/${x.id}`}
          total={paged.total}
          hasMore={paged.hasMore}
          onLoadMore={paged.loadMore}
          isLoadingMore={paged.isLoadingMore}
          emptyMessage={t("jobTemplates.empty")}
        />
      )}
    </div>
  );
}
